import { useModalPortalTarget } from '@amelio/ui-library';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { MoreVerticalIcon } from './icons';

export type ActionMenuItem = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    danger?: boolean;
    disabled?: boolean;
    tooltip?: string;
    onClick: () => void;
};

type ActionMenuProps = {
    items: ActionMenuItem[];
    triggerClassName?: string;
    ariaLabel?: string;
    dataTestId?: string;
};

const TRIGGER_CLASS =
    'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded border border-[#e0e3f0] bg-white transition-colors hover:bg-gray-50';

const MENU_WIDTH = 224; // w-56
const MENU_GAP = 4;

export const ActionMenu = ({ items, triggerClassName, ariaLabel, dataTestId }: ActionMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const portalTarget = useModalPortalTarget();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const path = event.composedPath();
            const insideTrigger = containerRef.current && path.includes(containerRef.current);
            const insideMenu = menuRef.current && path.includes(menuRef.current);
            if (!insideTrigger && !insideMenu) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen || !triggerRef.current) {
            return;
        }

        const updatePosition = () => {
            const trigger = triggerRef.current;
            if (!trigger) {
                return;
            }

            const rect = trigger.getBoundingClientRect();
            setPosition({
                top: rect.bottom + MENU_GAP,
                left: rect.right - MENU_WIDTH,
            });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    const menu = (
        <div
            ref={menuRef}
            className={
                'pointer-events-auto fixed z-[1000] w-56 overflow-hidden rounded-lg border border-gray-100 bg-white py-1 shadow-lg'
            }
            style={{ top: position?.top ?? 0, left: position?.left ?? 0 }}
            role={'menu'}
            onClick={(event) => event.stopPropagation()}
        >
            {items.map((item) => (
                <button
                    key={item.id}
                    type={'button'}
                    disabled={item.disabled}
                    title={item.tooltip}
                    onClick={() => {
                        if (item.disabled) {
                            return;
                        }

                        item.onClick();
                        setIsOpen(false);
                    }}
                    className={
                        'text-body-sm flex w-full items-center gap-2 px-4 py-2 text-left transition-colors ' +
                        (item.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-[#f5f7f9]') +
                        ' ' +
                        (item.danger ? 'text-error' : 'text-navy')
                    }
                    role={'menuitem'}
                >
                    {item.icon && (
                        <span className={'flex h-4 w-4 items-center justify-center'}>
                            {item.icon}
                        </span>
                    )}

                    {item.label}
                </button>
            ))}
        </div>
    );

    return (
        <div
            className={'relative'}
            ref={containerRef}
        >
            <button
                ref={triggerRef}
                type={'button'}
                onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen((previous) => !previous);
                }}
                className={`${TRIGGER_CLASS}${triggerClassName ? ` ${triggerClassName}` : ''}`}
                aria-label={ariaLabel}
                data-testid={dataTestId}
            >
                <MoreVerticalIcon className={'text-navy h-4 w-4'} />
            </button>

            {isOpen &&
                position &&
                (portalTarget
                    ? createPortal(menu, portalTarget)
                    : createPortal(menu, document.body))}
        </div>
    );
};
